/**
 * Structured data utilities for SEO
 * These functions generate JSON-LD structured data for different page types
 */

/**
 * Generate structured data for the homepage
 */
export function generateHomePageStructuredData() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "SOKASTIFTELSEMEDEL.SE",
    "url": "https://www.sokastiftelsemedel.se/",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://www.sokastiftelsemedel.se/?q={search_term_string}",
      "query-input": "required name=search_term_string"
    },
    "description": "Sök bland svenska stiftelser och hitta finansiering för ditt ändamål. En omfattande databas med över 17 000 stiftelser i Sverige."
  };
}

/**
 * Generate structured data for an article/blog post
 */
export function generateArticleStructuredData(
  title: string,
  description: string,
  url: string,
  imageUrl: string,
  datePublished: string,
  dateModified: string,
  authorName: string = "SOKASTIFTELSEMEDEL.SE"
) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": imageUrl,
    "datePublished": datePublished,
    "dateModified": dateModified,
    "author": {
      "@type": "Organization",
      "name": authorName
    },
    "publisher": {
      "@type": "Organization",
      "name": "SOKASTIFTELSEMEDEL.SE",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.sokastiftelsemedel.se/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url
    }
  };
}

/**
 * Generate structured data for a foundation
 */
export function generateFoundationStructuredData(
  name: string,
  description: string,
  url: string,
  region: string,
  yearEstablished?: number
) {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": name,
    "description": description,
    "url": url,
    "foundingDate": yearEstablished ? yearEstablished.toString() : undefined,
    "location": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressRegion": region,
        "addressCountry": "SE"
      }
    }
  };
}

/**
 * Generate structured data for the FAQ page
 */
export function generateFAQStructuredData(
  faqs: Array<{ question: string; answer: string }>
) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
}

/**
 * Generate structured data for the organization
 */
export function generateOrganizationStructuredData() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "SOKASTIFTELSEMEDEL.SE",
    "url": "https://www.sokastiftelsemedel.se",
    "logo": "https://www.sokastiftelsemedel.se/logo.png",
    "sameAs": [
      "https://www.facebook.com/sokastiftelsemedel",
      "https://www.linkedin.com/company/sokastiftelsemedel"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer support",
      "email": "info@sokastiftelsemedel.se"
    }
  };
}

/**
 * Generate structured data for breadcrumbs
 */
export function generateBreadcrumbStructuredData(
  breadcrumbs: Array<{ name: string; url: string }>
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((breadcrumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": breadcrumb.name,
      "item": breadcrumb.url
    }))
  };
}

/**
 * Generate structured data for HowTo guides
 */
export function generateHowToStructuredData(
  name: string,
  description: string,
  imageUrl: string,
  steps: Array<{
    name: string;
    text: string;
    url?: string;
    imageUrl?: string;
  }>,
  totalTime?: string
) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": name,
    "description": description,
    "image": imageUrl,
    ...(totalTime && { "totalTime": totalTime }),
    "step": steps.map((step, index) => ({
      "@type": "HowToStep",
      "url": step.url || undefined,
      "image": step.imageUrl || undefined,
      "name": step.name,
      "text": step.text,
      "position": index + 1
    }))
  };
}

/**
 * Generate structured data for LocalBusiness
 */
export function generateLocalBusinessStructuredData() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "SOKASTIFTELSEMEDEL.SE",
    "url": "https://www.sokastiftelsemedel.se",
    "logo": "https://www.sokastiftelsemedel.se/logo.png",
    "image": "https://www.sokastiftelsemedel.se/images/office.jpg",
    "description": "Sök bland svenska stiftelser och hitta finansiering för ditt ändamål. En omfattande databas med över 17 000 stiftelser i Sverige.",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "SE",
      "addressRegion": "Stockholm"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "59.3293",
      "longitude": "18.0686"
    },
    "telephone": "+46-XX-XXX-XX-XX",
    "email": "info@sokastiftelsemedel.se",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday"
        ],
        "opens": "09:00",
        "closes": "17:00"
      }
    ],
    "priceRange": "$$"
  };
}